import { atom } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import request from 'lib/request';
import type {
  InterestDetailResponse,
  InterestOverviewResponse,
} from '../types-interest';
import { buildSectionMutationAtom } from 'lib/atoms/section-mutation';

// ---------------------------------------------------------------------------
// DO Interest Review data layer. Mirrors `afe-catalog/structuring/atoms/costs.ts`:
//   • the Overview tiles + per-line table live in a module-level atom so the
//     overview and any mutation can share one piece of state;
//   • the per-interest detail uses the codebase `createResourceAtom` factory
//     (auto-fetches on mount, setter refetches);
//   • edit / create / remove / restore / confirm / revert are uniform
//     { url, method, data } mutation atoms built from the SHARED
//     `buildSectionMutationAtom` (imported — never recreated).
//
// The DO drill-in SELECTION state is DOCUMENT-SCOPED and lives in `./layout`
// (a separate fileplan item) — it is intentionally NOT declared here so DO does
// not reuse AFE's module-scoped drill-in singletons.
// ---------------------------------------------------------------------------

// ── Overview (GET /documents/:documentId/division_order_structuring/interests) ─

export const interestOverviewDataAtom = atom<InterestOverviewResponse | null>(
  null
);
export const interestOverviewLoadingAtom = atom(false);
// Load-failure message. Non-null means the last fetch failed — the section shows
// an error banner instead of an empty table, so a 5xx/network failure is never
// silently indistinguishable from a DO that genuinely has no interest lines
// (this data gates catalog trust, so "looks empty" must not read as "is empty").
export const interestOverviewErrorAtom = atom<string | null>(null);

// The most-recent fetch target. A stale in-flight response (e.g. the previous
// document's fetch resolving after the shell switched documents, or an older
// reload superseded by a newer one) must NOT commit over the current data — it
// is dropped unless it is still the latest requested href. The data atom is
// module-level (shared across DO documents), so without this guard document A's
// slow response could clobber document B's freshly-loaded lines.
const interestOverviewRequestedHrefAtom = atom<string | undefined>(undefined);

export const fetchInterestOverviewAtom = atom(
  null,
  async (get, set, href: string | undefined) => {
    if (!href) return;
    set(interestOverviewRequestedHrefAtom, href);
    set(interestOverviewLoadingAtom, true);
    set(interestOverviewErrorAtom, null);
    try {
      const response = await request.get<InterestOverviewResponse>(href);
      // Drop a response that is no longer the latest requested target.
      if (get(interestOverviewRequestedHrefAtom) !== href) return;
      if ('error' in response) {
        console.error('Failed to fetch DO interest overview:', response.error);
        set(interestOverviewErrorAtom, 'Unable to load interest lines.');
      } else {
        set(interestOverviewDataAtom, response);
      }
    } catch (error) {
      if (get(interestOverviewRequestedHrefAtom) !== href) return;
      console.error('Failed to fetch DO interest overview:', error);
      set(interestOverviewErrorAtom, 'Unable to load interest lines.');
    } finally {
      // Only the latest fetch clears the loading flag, so it stays true while a
      // superseding fetch is still in flight.
      if (get(interestOverviewRequestedHrefAtom) === href) {
        set(interestOverviewLoadingAtom, false);
      }
    }
  }
);

export const interestOverviewUrl = (documentId: number | string) =>
  `/documents/${documentId}/division_order_structuring/interests`;

// ── Interest detail (GET .../interests/:lineItemId — lineItemId = DOLI SF Id) ──
//
// The line item id is a Salesforce Id STRING, not a numeric PK — keep it a
// string all the way through the URL builder + resource atom.

export const createInterestDetailResourceAtom = (href: string | undefined) =>
  createResourceAtom<InterestDetailResponse>(href);

export const interestDetailUrl = (
  documentId: number | string,
  lineItemId: string
) =>
  `/documents/${documentId}/division_order_structuring/interests/${lineItemId}`;

// ── Lifecycle / edit mutations (SHARED buildSectionMutationAtom) ─────────────

/** Edit one interest line in place (PATCH .../interests/:id). */
export const editInterestAtom =
  buildSectionMutationAtom<InterestDetailResponse>();

/** Add a new interest line (POST .../interests). */
export const createInterestAtom =
  buildSectionMutationAtom<InterestDetailResponse>();

/** Soft-delete one interest line (DELETE .../interests/:id). */
export const removeInterestAtom =
  buildSectionMutationAtom<InterestDetailResponse>();

/** Undo the soft-delete (POST resources.restoration.href). */
export const restoreInterestAtom =
  buildSectionMutationAtom<InterestDetailResponse>();

/** Mark as Correct (POST resources.confirmation.href). */
export const confirmInterestAtom =
  buildSectionMutationAtom<InterestDetailResponse>();

/** Remove Correct Status (DELETE resources.confirmation.href). */
export const revertInterestAtom =
  buildSectionMutationAtom<InterestDetailResponse>();
